/**
 * JurisdictionPicker
 * Cascading country → jurisdiction selector with "Other" fallback.
 * Selected items are added as tags.
 * Defined outside any parent component to avoid remount-on-render focus loss.
 */
import React, { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import {
  fetchCountries,
  fetchJurisdictionsByCountry,
  CountryOption,
  JurisdictionOption,
} from '../../../api/enterpriseApi';
import styles from './JurisdictionPicker.module.css';

interface Props {
  label: string;
  tags: string[];
  onAdd: (value: string) => void;
  onRemove: (index: number) => void;
}

export function JurisdictionPicker({ label, tags, onAdd, onRemove }: Props) {
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [jurisdictions, setJurisdictions] = useState<JurisdictionOption[]>([]);
  const [jurisLoading, setJurisLoading] = useState(false);

  const [selectedCountryId, setSelectedCountryId] = useState('');
  const [selectedCountryName, setSelectedCountryName] = useState('');
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState('');
  const [selectedJurisdictionName, setSelectedJurisdictionName] = useState('');
  const [isOtherCountry, setIsOtherCountry] = useState(false);
  const [isOtherJurisdiction, setIsOtherJurisdiction] = useState(false);
  const [customCountry, setCustomCountry] = useState('');
  const [customJurisdiction, setCustomJurisdiction] = useState('');

  useEffect(() => {
    fetchCountries().then(setCountries).catch(() => {});
  }, []);

  const handleCountryChange = async (countryId: string, countryName: string) => {
    if (countryId === '__other__') {
      setIsOtherCountry(true);
      setSelectedCountryId('__other__');
      setSelectedCountryName('');
      setJurisdictions([]);
      setSelectedJurisdictionId('');
      setSelectedJurisdictionName('');
      setIsOtherJurisdiction(false);
      return;
    }
    setIsOtherCountry(false);
    setSelectedCountryId(countryId);
    setSelectedCountryName(countryName);
    setSelectedJurisdictionId('');
    setSelectedJurisdictionName('');
    setIsOtherJurisdiction(false);
    setJurisdictions([]);
    if (!countryId) return;
    setJurisLoading(true);
    try {
      const jurs = await fetchJurisdictionsByCountry(countryId);
      setJurisdictions(jurs);
    } catch { /* ignore */ }
    finally { setJurisLoading(false); }
  };

  const handleJurisdictionChange = (id: string, name: string) => {
    if (id === '__other__') {
      setIsOtherJurisdiction(true);
      setSelectedJurisdictionId('__other__');
      setSelectedJurisdictionName('');
    } else {
      setIsOtherJurisdiction(false);
      setSelectedJurisdictionId(id);
      setSelectedJurisdictionName(name);
    }
  };

  const resolvedValue = (): string => {
    if (isOtherCountry) {
      return [customCountry, customJurisdiction].filter(Boolean).join(' – ');
    }
    const jur = isOtherJurisdiction ? customJurisdiction : selectedJurisdictionName;
    return [selectedCountryName, jur].filter(Boolean).join(' – ');
  };

  const canAdd = (): boolean => {
    const v = resolvedValue().trim();
    return v.length > 0 && !tags.includes(v);
  };

  const handleAdd = () => {
    const v = resolvedValue().trim();
    if (!v) return;
    onAdd(v);
    // Reset selectors for next entry
    setSelectedCountryId('');
    setSelectedCountryName('');
    setSelectedJurisdictionId('');
    setSelectedJurisdictionName('');
    setIsOtherCountry(false);
    setIsOtherJurisdiction(false);
    setCustomCountry('');
    setCustomJurisdiction('');
    setJurisdictions([]);
  };

  return (
    <div className={styles.root}>
      <label className={styles.label}>{label}</label>

      {/* Country */}
      <div className={styles.row}>
        <select
          value={selectedCountryId}
          onChange={e => {
            const opt = e.target.options[e.target.selectedIndex];
            handleCountryChange(e.target.value, opt.dataset.name || '');
          }}
          className={styles.select}
        >
          <option value="">Select country…</option>
          {countries.map(c => (
            <option key={c.id} value={c.id} data-name={c.name}>{c.name}</option>
          ))}
          <option value="__other__">Other (not listed)</option>
        </select>
      </div>

      {/* Other country free-text */}
      {isOtherCountry && (
        <input
          className={styles.input}
          value={customCountry}
          onChange={e => setCustomCountry(e.target.value)}
          placeholder="Enter country name"
        />
      )}

      {/* Jurisdiction dropdown */}
      {!isOtherCountry && selectedCountryId && (
        jurisLoading ? (
          <div className={styles.loading}>Loading jurisdictions…</div>
        ) : (
          <select
            value={selectedJurisdictionId}
            onChange={e => {
              const opt = e.target.options[e.target.selectedIndex];
              handleJurisdictionChange(e.target.value, opt.dataset.name || '');
            }}
            className={styles.select}
          >
            <option value="">Select jurisdiction…</option>
            {jurisdictions.map(j => (
              <option key={j.id} value={j.id} data-name={j.name}>
                {j.name}{j.type ? ` (${j.type})` : ''}
              </option>
            ))}
            <option value="__other__">Other (not listed)</option>
          </select>
        )
      )}

      {/* Other jurisdiction free-text */}
      {(isOtherJurisdiction || (isOtherCountry && selectedCountryId !== '')) && (
        <input
          className={styles.input}
          value={customJurisdiction}
          onChange={e => setCustomJurisdiction(e.target.value)}
          placeholder="Enter jurisdiction / state / region"
        />
      )}

      {/* Preview + Add */}
      {resolvedValue() && (
        <div className={styles.previewRow}>
          <span className={styles.preview}><MapPin size={16} aria-hidden /> {resolvedValue()}</span>
          <button
            type="button"
            className={styles.addBtn}
            onClick={handleAdd}
            disabled={!canAdd()}
          >
            Add
          </button>
        </div>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className={styles.tags}>
          {tags.map((t, i) => (
            <span key={i} className={styles.tag}>
              <MapPin size={16} aria-hidden /> {t}
              <button type="button" onClick={() => onRemove(i)}>×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
